/**
 * @file schemas.ts
 * @brief Zod schemas + types for the Super Admin → Languages feature.
 *
 * @details
 * Backed by `BACKEND/src/features/admin/routes.js::GET /api/admin/languages`
 * which returns the full `SUPPORTED_LANGS` catalogue merged with the
 * `language_settings` table (per-locale enabled flag + audit metadata).
 *
 * The same row shape is returned by `getAllLanguagesWithStatus()` in
 * `BACKEND/src/features/s2s/languages.mjs`. `enabled` defaults to true
 * for any language that doesn't yet have a row in `language_settings`.
 *
 * @author DES
 * @date 26-05-2026
 */

import { z } from "zod";

export const LanguageRowSchema = z.object({
  code: z.string(),
  locale: z.string().nullable().optional().default(""),
  label: z.string(),
  enabled: z.boolean().default(true),
  reason: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  updated_by: z.string().nullable().optional(),
});

export type LanguageRow = z.infer<typeof LanguageRowSchema>;

export const LanguagesResponseSchema = z.object({
  languages: z.array(LanguageRowSchema),
});

export type LanguagesResponse = z.infer<typeof LanguagesResponseSchema>;

// ----------------------------------------------------------------------
// Mutation payload — `code` is required, `locale` is empty string for
// languages that don't ship multiple locales (matches backend's
// normalised key).
// ----------------------------------------------------------------------
export const UpdateLanguageStatusSchema = z.object({
  code: z.string(),
  locale: z.string().optional(),
  enabled: z.boolean(),
  reason: z.string().optional(),
});

export type UpdateLanguageStatusInput = z.infer<
  typeof UpdateLanguageStatusSchema
>;
